import React, { useMemo } from 'react'

import { useChatMessage } from 'store/chat/chat-message/chat-message-hooks'
import { ContainerName, useAppSelector, useAppUser } from 'components/_hooks'
import { makeGetChat } from 'store/chat/chat/chat-selectors'
import { useOpenChat, useOpenUserChat } from 'store/chat/chat/chat-hooks'


import styles from './ChatGroupContainer.module.scss'
import chatPersonStyles from '../ChatPerson/ChatPerson.module.scss'
import chatGroupStyles from 'components/_shared/Chat/_shared/ChatGroupContainer/ChatGroupContainer.module.scss'
import chatListStyles from 'components/_shared/Chat/_pages/ChatList/ChatList.module.scss'


import { makeGetUser } from 'store/selectors/user-selectors'
import { makeGetChatMemeber } from 'store/chat/chat-member/chat-member-selectors'
import { ChatType } from 'consts'
import { UserAvatar } from 'components/_shared/UserAvatar/UserAvatar'
import { cn, defaultFormatDateTime } from 'utils'
import { useDispatch } from 'react-redux'
import { loadChat } from 'store/chat/chat/chat-logic'
import { getGroupChatName } from 'store/chat/chat/chat-types'
import { ChatMessageTypeEnum } from 'store/chat/chat-message/chat-message-types'
import { useChatMembers } from 'store/chat/chat-member/chat-member-hooks'
import { makeGetUnreadMessagesCountByChatId } from 'store/chat/chat-message/chat-message-selectors'


interface Props {
  messageId: number
  onClick: CallableFunction
}

const ChatGroupContainer = ({messageId, onClick}: Props) => {

  const dispatch = useDispatch()
  const chatMessage = useChatMessage(messageId)
  const getChat = useMemo(makeGetChat, [])
  const chat = useAppSelector(state => getChat(state, {chatId: chatMessage?.ChatId}))

  const getChatMember = useMemo(makeGetChatMemeber, [])
  const chatMember = useAppSelector(state => getChatMember(state, { chatMemberId: chatMessage?.ChatMemberId }))

  const getUser = useMemo(makeGetUser, [])
  const user = useAppSelector(state => getUser(state, chatMember?.UserId))
  const appUser = useAppUser()


  const openChat = useOpenChat()
  const openUserChat = useOpenUserChat()

  const chatMembers = useChatMembers(chat?.Id)  
  const opponentChatMember = useMemo(() =>  {
    if (chat?.Type != ChatType.Private) {
      return null
    } 
    return chatMembers.find(cm => cm.UserId != appUser?.Id)
  }, [chatMembers, appUser?.Id, chat?.Type])

  const opponentUser = useAppSelector(state => getUser(state, opponentChatMember?.UserId))
  const getUnreadMessagesCount = useMemo(makeGetUnreadMessagesCountByChatId, [])
  const unreadMessagesCount = useAppSelector(state => getUnreadMessagesCount(state,{chatId:chat?.Id}))


  const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault()
    
    dispatch(loadChat(chat.Id))
    onClick()
    if (chat.Type == ChatType.Group) {
      openChat(chat.Id)
    } else {
      if (opponentUser.Id != appUser.Id) {
        openUserChat(opponentUser.Id)
      } else {
        openChat(chat.Id)
      }
    }
  }

  return (
    <section className={styles.chatPersonContainer}>
      <div className="row m-0-15 align-items-stretch h-100">
        <div className="col-auto p-0 d-flex align-items-center position-relative">
          {chat?.Type == ChatType.Group ? (
            <div className={cn(chatPersonStyles.chatPersonPicture, chatGroupStyles.chatPersonPictureGroup)}>
              {getGroupChatName(chat?.RelationType)}
            </div>
          ) : (
            <>
              <UserAvatar containerName={ContainerName.AvatarsNormal}
                userId={opponentUser?.Id} 
                className={cn(chatPersonStyles.chatPersonPicture)} 
                placeholder="dark"
              />              
              <div className={cn(chatPersonStyles.chatPersonStatus, opponentUser?.IsOnline && chatPersonStyles.online)} />
            </>
          )}
        
        </div>
        <div className="col d-flex flex-column justify-content-center p-0">
          <div className={cn(chatPersonStyles.chatPersonName, styles.chatGroup)}>{chat?.Name || opponentUser?.Username || opponentUser?.Organizationname}</div>
          {chatMessage && (
            <div className={cn(chatPersonStyles.chatPersonMessage, styles.unreadMessage)}>
              <>
                {chat?.Type == ChatType.Group && chatMessage.ChatMessageType == ChatMessageTypeEnum.Standard && (
                  <span className={styles.chatPreviewUser}>{`${user?.Username || user?.Organizationname}:`}</span>
                )}
                <span>
                  {chatMessage.Text}
                </span>
              </>
            </div>
          )}
        </div>
        <div className="col-auto p-0">
          <div className={styles.chatPersonDate}>{defaultFormatDateTime(chatMessage.SentDate)}</div>
          {unreadMessagesCount && <div className={chatListStyles.unreadMessageCount}>{unreadMessagesCount.unreadMessagesCount}</div>}
        </div>
      </div>
      <a className={styles.chatPersonLink} href="#" onClick={handleClick} />
    </section>
  )
}

export default ChatGroupContainer
